/**
 * Profile store — multiple birth charts in one browser.
 *
 * The app originally kept a single profile in flat localStorage keys
 * ('birthData', 'chartData', 'conversationHistory', ...). This module replaces
 * that with a list of profiles under one key, and migrates the old layout on
 * first load so existing users don't lose their chart or chat history.
 *
 * Everything is local to the browser: no accounts, no server-side storage.
 */

const STORE_KEY = 'astrobot.profiles.v1';
const ACTIVE_KEY = 'astrobot.activeProfileId';

// Legacy single-profile keys, migrated then left in place as a safety net.
const LEGACY_KEYS = {
  step: 'appStep',
  birthData: 'birthData',
  chartData: 'chartData',
  interests: 'userInterests',
  history: 'conversationHistory',
  formDraft: 'birthDetails',
};

function safeParse(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.error('profiles: could not parse stored value', e);
    return fallback;
  }
}

export function createProfileId() {
  // crypto.randomUUID isn't available on older Safari / non-secure origins.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Derive a display name. Users never typed a name in the original flow, so
 * fall back to something recognisable rather than showing "Untitled".
 */
export function deriveProfileName(profile) {
  // The person's own name, as entered on the birth-details form.
  if (profile?.name && profile.name.trim()) return profile.name.trim();
  if (profile?.birthData?.name && profile.birthData.name.trim()) {
    return profile.birthData.name.trim();
  }
  // Never fall back to the birth place: profiles created before the name field
  // existed would otherwise be addressed by their city ("Namaste, Karnal").
  if (profile?.birthData?.date) return `Born ${profile.birthData.date}`;
  return 'New profile';
}

export function readStore() {
  const store = safeParse(localStorage.getItem(STORE_KEY), null);
  if (store && Array.isArray(store.profiles)) return store;
  return { profiles: [] };
}

export function writeStore(store) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch (e) {
    // Quota exceeded — most likely long chat histories on many profiles.
    console.error('profiles: failed to persist store', e);
  }
}

export function getActiveProfileId() {
  return localStorage.getItem(ACTIVE_KEY) || null;
}

export function setActiveProfileId(id) {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

export function blankProfile(overrides = {}) {
  return {
    id: createProfileId(),
    name: '',
    createdAt: new Date().toISOString(),
    birthData: null,
    chartData: null,
    interests: [],
    messages: [],
    // Per-profile position in the flow, so a half-finished profile resumes
    // where it left off instead of dropping the user into an empty chat.
    step: 'birth-details',
    ...overrides,
  };
}

/**
 * Pull the old single-profile keys into the new store. Runs once; afterwards
 * the store exists and this is a no-op.
 */
function migrateLegacyProfile() {
  const birthData = safeParse(localStorage.getItem(LEGACY_KEYS.birthData), null);
  const chartData = safeParse(localStorage.getItem(LEGACY_KEYS.chartData), null);
  if (!birthData && !chartData) return null;

  const messages = safeParse(localStorage.getItem(LEGACY_KEYS.history), []) || [];
  const interests = safeParse(localStorage.getItem(LEGACY_KEYS.interests), []) || [];
  const legacyStep = localStorage.getItem(LEGACY_KEYS.step);

  const profile = blankProfile({
    birthData,
    chartData,
    interests,
    messages,
    step: legacyStep || (chartData ? 'chat' : 'birth-details'),
  });
  // Leave `name` empty: legacy profiles predate the name field, so there is no
  // real name to migrate. deriveProfileName falls back to a birth-date label
  // until the user renames the profile or re-saves their details.
  return profile;
}

/**
 * Load the store, migrating legacy data if this is the first run.
 * Always returns a store with at least one profile and a valid active id.
 */
export function loadProfiles() {
  let store = readStore();

  if (store.profiles.length === 0) {
    const migrated = migrateLegacyProfile();
    store = { profiles: migrated ? [migrated] : [blankProfile()] };
    writeStore(store);
  }

  let activeId = getActiveProfileId();
  if (!activeId || !store.profiles.some((p) => p.id === activeId)) {
    activeId = store.profiles[0].id;
    setActiveProfileId(activeId);
  }

  return { profiles: store.profiles, activeId };
}

/** A compact "Leo · Ketu" descriptor for the switcher rows. */
export function profileSubtitle(profile) {
  const asc = profile?.chartData?.houses?.ascendant?.sign;
  const dasha = profile?.chartData?.dasha?.current_mahadasha?.lord;
  if (asc && dasha) return `${asc} · ${dasha}`;
  if (asc) return asc;
  if (profile?.birthData?.date) return profile.birthData.date;
  return 'Chart not calculated';
}

/** Initials for the avatar chip. */
export function profileInitials(profile) {
  const name = deriveProfileName(profile);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
